import React, { useEffect, useRef, useCallback } from 'react';

const CustomCursor = () => {
  const cursorRef = useRef(null);
  const trailContainerRef = useRef(null);
  const mousePos = useRef({ x: -100, y: -100 });
  const isVisible = useRef(false);
  const particlePool = useRef([]);
  const particleIndex = useRef(0);
  const lastSpawnTime = useRef(0);
  const rafId = useRef(null);
  
  // Initialize particle pool
  const initParticles = useCallback(() => {
    if (!trailContainerRef.current) return;
    
    // Create 30 reusable particle elements
    for (let i = 0; i < 30; i++) {
      const particle = document.createElement('div');
      particle.className = 'spark-particle';
      particle.style.cssText = `
        position: fixed;
        width: 12px;
        height: 12px;
        background: radial-gradient(circle, rgba(255, 200, 100, 1) 0%, rgba(255, 150, 50, 0.8) 40%, rgba(255, 100, 20, 0.4) 70%, transparent 100%);
        border-radius: 50%;
        pointer-events: none;
        z-index: 9998;
        box-shadow: 0 0 15px rgba(255, 180, 100, 0.9), 0 0 30px rgba(255, 150, 50, 0.6), 0 0 45px rgba(255, 100, 20, 0.3);
        opacity: 0;
        will-change: transform, opacity;
      `;
      trailContainerRef.current.appendChild(particle);
      particlePool.current.push({
        element: particle,
        active: false,
        startTime: 0,
        x: 0,
        y: 0
      });
    }
  }, []);
  
  // Spawn a new particle
  const spawnParticle = useCallback((x, y) => {
    const pool = particlePool.current;
    if (pool.length === 0) return;
    
    // Find next available particle (round-robin)
    const particle = pool[particleIndex.current % pool.length];
    particleIndex.current++;
    
    particle.x = x;
    particle.y = y;
    particle.startTime = performance.now();
    particle.active = true;
    particle.element.style.left = `${x}px`;
    particle.element.style.top = `${y}px`;
  }, []);
  
  // Animation loop for particles
  const animateParticles = useCallback(() => {
    const now = performance.now();
    const lifetime = 800; // Particle lifetime in ms
    
    particlePool.current.forEach(particle => {
      if (!particle.active) {
        particle.element.style.opacity = '0';
        return;
      }
      
      const age = now - particle.startTime;
      const life = 1 - (age / lifetime);
      
      if (life <= 0) {
        particle.active = false;
        particle.element.style.opacity = '0';
      } else {
        particle.element.style.opacity = String(life * 0.9);
        particle.element.style.transform = `translate(-50%, -50%) scale(${0.3 + life * 0.7})`;
      }
    });
    
    rafId.current = requestAnimationFrame(animateParticles);
  }, []);
  
  useEffect(() => {
    initParticles();
    rafId.current = requestAnimationFrame(animateParticles);
    
    const handleMouseMove = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
      
      // Update cursor position directly (no React state for instant response)
      if (cursorRef.current) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
        cursorRef.current.style.opacity = '1';
      }
      
      // Spawn particles at intervals (every 20ms for smooth trail)
      const now = performance.now();
      if (now - lastSpawnTime.current > 20) {
        spawnParticle(e.clientX, e.clientY);
        lastSpawnTime.current = now;
      }
      
      isVisible.current = true;
    };
    
    const handleMouseLeave = () => {
      isVisible.current = false;
      if (cursorRef.current) {
        cursorRef.current.style.opacity = '0';
      }
    };
    
    const handleMouseEnter = () => {
      isVisible.current = true;
      if (cursorRef.current) {
        cursorRef.current.style.opacity = '1';
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [initParticles, animateParticles, spawnParticle]);
  
  return (
    <>
      {/* Trail Container */}
      <div ref={trailContainerRef} style={{ position: 'fixed', top: 0, left: 0, pointerEvents: 'none', zIndex: 9998 }} />
      
      {/* Blackhole Cursor */}
      <div
        ref={cursorRef}
        className="blackhole-cursor"
        style={{
          left: -100,
          top: -100,
          opacity: 0,
        }}
      >
        <div className="blackhole-core" />
        <div className="blackhole-ring" />
        <div className="blackhole-ring-outer" />
      </div>
    </>
  );
};

export default CustomCursor;
